import type { ICalendarDisconnectedNotificationData } from "@frontend-shared/types";
import { CalendarX } from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";

interface ICalendarDisconnectedCardProps {
  uuid: string;
  data: ICalendarDisconnectedNotificationData;
  createdAt: string;
  isRead: boolean;
  onReconnect: () => void;
}

const CalendarDisconnectedCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onReconnect,
}: ICalendarDisconnectedCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  // NOTE: Prefer the user-chosen `displayName` over the raw `email`, since
  // EWS/iCal connections store an opaque mailbox identifier in `email`
  // (a base64-encoded GUID) that's meaningless to the user. Fall back to
  // `email` when no display name was set, and to a no-label body when
  // neither is available. Keeping the i18n keys literal (not
  // `t(dynamicString)`) so i18next's strict-key typing accepts the call.
  const accountLabel = data.displayName ?? data.email;
  const body = accountLabel
    ? t("type.calendar_disconnected.body_with_label", {
        providerLabel: data.providerLabel,
        accountLabel,
      })
    : t("type.calendar_disconnected.body", {
        providerLabel: data.providerLabel,
      });

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="calendar_disconnected"
      createdAt={createdAt}
      isRead={isRead}
      icon={<CalendarX className="h-5 w-5 text-amber-500" />}
      iconBgClassName="bg-amber-500/10"
      ctaLabel={t("reconnect_calendar")}
      onCtaClick={onReconnect}
      body={body}
    />
  );
};

export const CalendarDisconnectedCard = memo(CalendarDisconnectedCardComponent);
